import React from "react";
import { TypographyProps as MuiTypographyProps } from "@mui/material";
import {
  TypographyLink,
  StyledTypography,
  TypographySize,
  TypographyAnchor,
} from "./Typography.styles";
import { ColorPalette } from "../../../theme";

export interface TypographyProps extends Omit<
  MuiTypographyProps,
  "children" | "content"
> {
  size: TypographySize;
  content: React.ReactNode;
  isBold: boolean;
  isCaps: boolean;
  isItalic: boolean;
  padding?: string;
  textColor?: keyof ColorPalette;
  externalLink?: boolean;
}

export interface TypographyOptions {
  content: React.ReactNode;
  size: TypographySize;
  isBold?: boolean;
  isCaps?: boolean;
  isItalic?: boolean;
  padding?: string;
  textColor?: keyof ColorPalette;
  link?: string;
  linkInNewWindow?: boolean;
  isDownload?: boolean;
  className?: string;
  externalLink?: boolean;
  onClick?: () => void;
  isEmail?: boolean;
  isPhone?: boolean;
  textDecoration?: string;
}

export const GetTypographyContent = (
  options: TypographyOptions,
): React.ReactElement => {
  const textColor = options.textColor || "neutral700";
  const typography = (
    <Typography
      size={options.size}
      isBold={options.isBold || false}
      isCaps={options.isCaps || false}
      isItalic={options.isItalic || false}
      padding={options.padding || "0"}
      content={options.content}
      textColor={textColor}
      className={options.className}
      onClick={options.onClick}
    />
  );
  if (options.isEmail) {
    return (
      <TypographyAnchor
        href={`mailto:${options.content}`}
        target="_blank"
        rel="noreferrer"
        $textColor={options.textColor || "redMedium"}
      >
        <Typography
          size={options.size}
          isBold={options.isBold || false}
          isCaps={options.isCaps || false}
          isItalic={options.isItalic || false}
          padding={options.padding || "0"}
          content={options.content}
          textColor={options.textColor || "redMedium"}
          className={options.className}
          onClick={options.onClick}
          externalLink={options.isEmail || options.externalLink || false}
        />
      </TypographyAnchor>
    );
  }
  if (options.isPhone) {
    return (
      <TypographyAnchor
        href={`tel:${options.content}`}
        target="_blank"
        rel="noreferrer"
        $textColor={options.textColor || "redMedium"}
      >
        <Typography
          size={options.size}
          isBold={options.isBold || false}
          isCaps={options.isCaps || false}
          isItalic={options.isItalic || false}
          padding={options.padding || "0"}
          content={options.content}
          textColor={options.textColor || "redMedium"}
          className={options.className}
          onClick={options.onClick}
          externalLink={options.isPhone || options.externalLink || false}
        />
      </TypographyAnchor>
    );
  }
  if (options.externalLink) {
    return (
      <TypographyAnchor
        href={options.link}
        target="_blank"
        rel="noreferrer"
        $textColor={options.textColor || "redMedium"}
      >
        <Typography
          size={options.size}
          isBold={options.isBold || false}
          isCaps={options.isCaps || false}
          isItalic={options.isItalic || false}
          padding={options.padding || "0"}
          content={options.content}
          textColor={textColor}
          className={options.className}
          onClick={options.onClick}
          externalLink={options.externalLink || false}
        />
      </TypographyAnchor>
    );
  }
  if (options.link) {
    if (options.linkInNewWindow) {
      return (
        <TypographyLink
          to={options.link}
          $textColor={textColor}
          target="_blank"
          download={options.isDownload}
          className={options.className}
        >
          {typography}
        </TypographyLink>
      );
    }
    return (
      <TypographyLink
        to={options.link}
        $textColor={textColor}
        $textDecoration={options.textDecoration}
        download={options.isDownload}
        className={options.className}
      >
        {typography}
      </TypographyLink>
    );
  }
  return typography;
};

export const Typography = (props: TypographyProps): React.ReactElement => (
  <StyledTypography
    size={props.size}
    $isBold={props.isBold}
    $isCaps={props.isCaps}
    $isItalic={props.isItalic}
    padding={props.padding}
    $textColor={props.textColor}
    className={props.className}
    onClick={props.onClick}
    $externalLink={props.externalLink}
  >
    {props.content}
  </StyledTypography>
);
